import type { GlobalFilter } from '@hotjar/global-filters';
import { convertFromNewUiToQueryFormat, FilterKindEnum } from '@hotjar/global-filters';
import { cloneDeep } from 'lodash-es';
import { v4 as generateUUID } from 'uuid';

import {
  mapDeviceToGlobalFilter,
  mapErrorOnPageToGlobalFilter,
  mapTargetingValueToRadioTextFilterOperation,
} from '../lib/mapHeatmapParamsToGlobalFilters';

export type GenerateNavigationLinkFromHeatmapsParams = {
  view: string;
  siteId: number;
  selectedFiltersInUiFormat: GlobalFilter;
  selectedDeviceType: HeatmapDeviceTypes;
  selectedTargetingValue: TargetingValue;
  searchPattern: string;
  applyErrorFilter?: boolean;
};

export const addDeviceTypeToGlobalFilters = (
  deviceType: HeatmapDeviceTypes,
  filters: GlobalFilter,
): void => {
  filters.filterList.push(mapDeviceToGlobalFilter(deviceType));
};

export const addTargetingRuleToGlobalFilters = (
  selectedTargetingValue: TargetingValue,
  searchPattern: string,
  filters: GlobalFilter,
): void => {
  const urlFilterOperation = mapTargetingValueToRadioTextFilterOperation(selectedTargetingValue);
  filters.filterList.push({
    id: generateUUID(),
    kind: FilterKindEnum.VisitedURL,
    value: {
      operation: urlFilterOperation,
      value: searchPattern,
    },
  });
};

export const addErrorFilterToGlobalFilters = (
  selectedTargetingValue: TargetingValue,
  searchPattern: string,
  filters: GlobalFilter,
): void => {
  filters.filterList.push(mapErrorOnPageToGlobalFilter(selectedTargetingValue, searchPattern));
};

export const generateNavigationLinkFromHeatmaps = ({
  view,
  siteId,
  selectedFiltersInUiFormat,
  selectedDeviceType,
  selectedTargetingValue,
  searchPattern,
  applyErrorFilter = false,
}: GenerateNavigationLinkFromHeatmapsParams): string => {
  /* This function receives the params that describe the heatmaps view state 
    (like applied filters, selected rules, etc.) and generates a link to corresponding view
    with the same filters applied.
    */

  const targetPathname = `/sites/${siteId}/${view}`;

  /* Heatmaps are filter by deviceType and targetingRule, so they are not applied in the filter chain.
  While creating the link, we need to manually add deviceType and targetingRule to applied filter chain.
    */

  // Deepcloning applied global filters to avoid state mutation.
  const clonedFilters = cloneDeep(selectedFiltersInUiFormat);

  addDeviceTypeToGlobalFilters(selectedDeviceType, clonedFilters);

  if (applyErrorFilter) {
    // Error filter contains the page subfilter already
    addErrorFilterToGlobalFilters(selectedTargetingValue, searchPattern, clonedFilters);
  } else {
    addTargetingRuleToGlobalFilters(selectedTargetingValue, searchPattern, clonedFilters);
  }

  const stringFilters = JSON.stringify(convertFromNewUiToQueryFormat(clonedFilters));
  const searchParams = new URLSearchParams({
    filters: stringFilters,
  });

  return new URL(`${targetPathname}?${searchParams}`, window.location.href).toString();
};
